/**
 * renderBlogContent — Unified blog content pipeline shared by both
 * the published Blog Detail page and the Admin Preview Modal so the two
 * render identically regardless of how the author stored the content.
 *
 * Pipeline:
 *   1. Detect format (HTML | Markdown | Plain Text) heuristically
 *   2. If Markdown → convert to HTML via marked
 *   3. If Plain Text with paragraph breaks → wrap each block in <p>
 *   4. Run enhanceShlokas() to auto-wrap Devanagari verses (`॥`/`ॐ ...`)
 *   5. Return HTML string ready for safeHtml() sanitization downstream.
 */
import { marked } from 'marked';
import { enhanceShlokas } from './shlokaEnhancer';
import { enhanceLabelledShlokas, enhanceAstroCallouts } from './astroBlogEnhancer';
import { normalizePoochoGurujiCTA } from './poochoGurujiCTA';
import { stripPlaceholderShlokas } from './stripPlaceholderShlokas';

// Configure marked once — GFM enabled, line breaks honored
marked.setOptions({
  gfm: true,
  breaks: true,
  headerIds: false,
  mangle: false,
});

function hasHtmlTags(text) {
  if (!text) return false;
  // Count opening tags for common block elements
  const blockTags = (text.match(/<(h[1-6]|p|ul|ol|li|table|blockquote|div|article)\b/gi) || []).length;
  return blockTags >= 2;
}

function looksLikeMarkdown(text) {
  if (!text) return false;
  // Markdown markers — at least two distinct signals
  let score = 0;
  if (/^#{1,6}\s+\S/m.test(text)) score += 2;        // ATX heading
  if (/^\s*[-*+]\s+\S/m.test(text)) score += 1;       // bullet list
  if (/^\s*\d+\.\s+\S/m.test(text)) score += 1;       // ordered list
  if (/\*\*[^*\n]+\*\*/.test(text)) score += 1;       // bold
  if (/(^|[^*])\*[^*\n]+\*([^*]|$)/.test(text)) score += 1; // italic
  if (/\[([^\]]+)\]\(([^)]+)\)/.test(text)) score += 1; // link
  if (/^>\s+\S/m.test(text)) score += 1;              // blockquote
  if (/^\s*\|.+\|\s*$/m.test(text)) score += 1;       // table row
  return score >= 2;
}

function wrapPlainTextParagraphs(text) {
  if (!text) return '';
  // Split on blank lines (one or more) → paragraphs
  const blocks = text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map(b => b.trim())
    .filter(Boolean);

  // If only one block, also fall back to single-newline splitting so that
  // line-broken-but-no-blank-line content (common in pasted text) still gets
  // sensible paragraph structure.
  const source = blocks.length > 1 ? blocks : text.split(/\n/).map(b => b.trim()).filter(Boolean);

  return source
    .map(b => `<p>${b.replace(/\n/g, '<br/>')}</p>`)
    .join('\n');
}

/**
 * Convert raw blog body into ready-to-sanitize HTML string.
 * Same logic applies to admin preview and live blog page.
 */
export function renderBlogContent(raw) {
  if (!raw || typeof raw !== 'string') return '';

  let html;
  if (hasHtmlTags(raw)) {
    // Already proper HTML — keep as-is
    html = raw;
  } else if (looksLikeMarkdown(raw)) {
    try {
      html = marked.parse(raw);
    } catch {
      html = wrapPlainTextParagraphs(raw);
    }
  } else {
    // Plain text — wrap paragraphs
    html = wrapPlainTextParagraphs(raw);
  }

  // Enhancement pipeline order matters:
  //   1. Labelled shlokas ("Shloka:" / "Source:" / "Meaning:" / "Interpretation:")
  //      → wrapped into premium .shloka.labelled card BEFORE plain-verse detector
  //      runs, so all four parts stay together.
  //   2. Plain-verse shlokas (Devanagari with ॥) → wrapped by legacy enhancer.
  //   3. Astro callouts (Takeaways / Medini / Country / Caution / Prediction /
  //      Zodiac Summary / Direct Answer) → wrap section groups. Runs LAST so
  //      shloka blocks act as boundaries for callout absorption.
  try { html = enhanceLabelledShlokas(html); } catch { /* non-fatal */ }
  try { html = enhanceShlokas(html); } catch { /* non-fatal */ }
  try { html = enhanceAstroCallouts(html); } catch { /* non-fatal */ }
  // 🚨 EEAT protection — strip any placeholder shloka blocks that legacy
  // blogs may have shipped ("श्लोक उपलब्ध नहीं है", "Coming Soon",
  // "Source: -", etc.). Must run AFTER enhancers so labelled cards are
  // detected in their final wrapped form.
  try { html = stripPlaceholderShlokas(html); } catch { /* non-fatal */ }
  // Universal Poocho Guruji CTA — normalize any legacy wording to the
  // Feb-07-2026 mandated copy + wire click-tracking hooks (button-only).
  try { html = normalizePoochoGurujiCTA(html); } catch { /* non-fatal */ }
  // Feb 12, 2026 (N11 SEO Fix) — Auto-blog LLM output sometimes contains an
  // `<h1>` inside the article body. BlogDetailPage renders its own page H1
  // for the title, so any body H1 makes the page have 2 H1s (Perplexity
  // flagged 46 blog posts with 2 H1). Downgrade all body H1s to H2 so the
  // page has exactly ONE semantic H1.
  try { html = html.replace(/<h1(\s[^>]*)?>/gi, '<h2$1>').replace(/<\/h1>/gi, '</h2>'); } catch { /* non-fatal */ }
  return html;
}

export default renderBlogContent;
